import React, { Fragment, useMemo, useState } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useAppContext } from 'Context/ReactAppContext';
import type { PayingHousehold } from 'Components/widgets/payment-info/PaymentInfo';
import { PaymentInfo } from 'Components/widgets/payment-info/PaymentInfo';
import {
  PaymentInfoProvider,
  type PaymentInfoContextType,
} from 'Components/widgets/payment-info/PaymentInfoContext';
import type { InfoBannerData } from 'Components/widgets/info-banner/infoBanner.query';
import { renderInfoBanner } from 'Components/widgets/info-banner/infoBanner.utils';
import { t } from 'Src/i18n';
import { getMainHouseholdAttendee } from 'Utils/proposal/proposalUtils';
import { FailedPaymentModal } from 'Views/booking/banking/FailedPaymentModal';
import { PaidBankingFormContent } from 'Views/booking/banking/PaidBankingFormContent';
import { Reassurance } from 'Views/booking/banking/Reassurance';
import { SimpleNotification } from 'Components/frameworks/notification/SimpleNotification';
import { Tabs } from 'Components/frameworks/tabs/Tabs';
import { useBankingView } from 'Views/booking/banking/useBankingView';
import { submitOptionFromProposal } from 'Components/widgets/contact-info/ContactInfoUtils';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import cookies from 'js-cookie';
import { SCOPE_SITE_SECTION } from 'Tracking/utils';
type Props = {
  i18n: I18N;
  selectedProposal: Proposal;
  infoBannerData: InfoBannerData;
  attendeesPaymentStatus: Array<AttendeePaymentStatus>;
  additionalProviders?: Array<PaymentProvider>;
  resort: ProductWithAccommodations;
  paymentProviders: Array<PaymentProvider>;
};

const B2BBankingView = ({
  i18n,
  selectedProposal,
  infoBannerData,
  attendeesPaymentStatus,
  additionalProviders,
  resort,
}: Props) => {
  const {
    openFailedPaymentModal,
    handleFailedPaymentModalClose,
    paymentInfoValue,
    paymentProviders,
    paymentProvidersWithValidMethods,
  } = useBankingView({
    selectedProposal,
    additionalProviders,
    resort,
    attendeesPaymentStatus,
  });
  const [selectedHouseholdIndex, setSelectedHouseholdIndex] = useState(0);
  const appContext = useAppContext();
  const { customerNeolaneId, isCxo, isCmta } = appContext;
  const { salesmanID } = useSelector((state) => state.seller.sellerInfo);
  const router = useRouter();
  const oneHouseholdOnly = selectedProposal.households.length === 1;
  const b2bPaymentInfoValue = useMemo<PaymentInfoContextType>(
    () => ({
      ...(paymentInfoValue as PaymentInfoContextType),
      selectedHouseholdIndex,
      sellerOptionRedirect: async (providerId: string) => {
        await submitOptionFromProposal({
          appContext,
          sellerEmail: appContext.cmta.email,
          providerId,
          selectedProposalId: selectedProposal.id,
          productId: selectedProposal.productId,
          hasSaveOption: false,
          router,
          salesmanID,
        })();
      },
    }),
    [paymentInfoValue, selectedHouseholdIndex],
  );

  if (isCmta) {
    cookies.set('cm_ta', 'true');
  }

  const { customerId = '' } = selectedProposal.households[selectedHouseholdIndex].attendees[0];
  cookies.set('customerIdOfSeller', customerNeolaneId || customerId);
  cookies.set('payment_entry_point', SCOPE_SITE_SECTION.BE);
  const tabsItems = selectedProposal.households.map((household, index) => {
    const hasBasePaymentData = household.paymentSchedule && paymentProviders.length > 0;
    const shouldShowPaymentInfo =
      isCxo && paymentProviders.length === 0 ? household.paymentSchedule : hasBasePaymentData;
    const currentHouseholdPaymentStatus = attendeesPaymentStatus.find(
      (bookingAttendee) => bookingAttendee.customerId === customerId,
    );
    const hasPaid = currentHouseholdPaymentStatus?.hasPaid || false;
    const mainAttendee = (getMainHouseholdAttendee(selectedProposal, index) || {}) as {
      firstName?: string;
      lastName?: string;
    };
    const payingHousehold: PayingHousehold = {
      paymentSchedule: household.paymentSchedule,
      customerId,
    };
    const withoutManualProviders = paymentProvidersWithValidMethods.filter(
      (provider) => !provider.shouldSkipPayment,
    );
    return {
      header: {
        title: (
          <div className="flex w-full flex-col justify-center text-14">
            <p className="text-xl mb-0 font-brand text-sienna">
              {t('booking.priceDetails.packagePrice.householdNumber', {
                householdNnumber: (index + 1).toString().padStart(2, '0'),
              })}
            </p>
            <p className="text-3xl mt-0 font-brand">
              {mainAttendee.firstName} {mainAttendee.lastName}
            </p>
          </div>
        ),
      },
      content: [
        shouldShowPaymentInfo
          ? selectedHouseholdIndex === index &&
            (hasPaid ? (
              <PaidBankingFormContent />
            ) : (
              <PaymentInfo
                i18n={i18n}
                paymentProviders={withoutManualProviders}
                paymentSchedule={household.paymentSchedule}
                price={household.paymentSchedule.total}
                payingHousehold={payingHousehold}
              />
            ))
          : null,
      ],
    };
  });
  return (
    <Fragment>
      {renderInfoBanner({
        infoBannerData,
        departureDate: selectedProposal.departureDate,
        arrivalDate: selectedProposal.arrivalDate,
        currentStep: 'banking',
        containerClassName: undefined,
        theme: undefined,
        contentClassName: undefined,
        popinPosition: undefined,
      })}
      <SimpleNotification
        id="BankingViewInformations"
        content={t('booking.banking.paymentEmergency')}
        iconName={ICONS.CLOCK_SPEED}
        className="mb-20"
      />
      {openFailedPaymentModal && <FailedPaymentModal onClose={handleFailedPaymentModalClose} />}
      <PaymentInfoProvider value={b2bPaymentInfoValue}>
        {oneHouseholdOnly ? (
          tabsItems[0]?.content
        ) : (
          <Tabs
            items={tabsItems}
            initialSelectedItem={oneHouseholdOnly ? 0 : undefined}
            onSelect={setSelectedHouseholdIndex}
            isContainerWide
            theme="default"
          />
        )}
      </PaymentInfoProvider>
      <Reassurance className="mt-20" i18n={i18n} />
    </Fragment>
  );
};

export { B2BBankingView };
